import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import axios from 'axios'
import { DOMAIN, api } from '../utils/api'
import { handleError } from '../utils/error'
import { currentUserActions } from '../redux/store'

export const useStatisticsSM = (token) => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const today = new Date()

	const [statistics, setStatistics] = useState([])
	const [users, setUsers] = useState([])
	const [loadingStatistics, setLoadingStatistics] = useState(true)
	const [timePick, setTimePick] = useState({
		date: today.getDate()?.toString(),
		month: (today.getMonth() + 1)?.toString(),
		year: today.getFullYear()?.toString(),
	})
	const [shift, setShift] = useState({ morning: true, afternoon: false })
	const [userFilter, setUserFilter] = useState('')
	const [orderFilter, setOrderFilter] = useState('')

	// Image viewer state
	const [currentImage, setCurrentImage] = useState(null)
	const [isViewerOpen, setIsViewerOpen] = useState(false)

	// Backdrop states
	const [showBackdrop, setShowBackdrop] = useState(false)
	const [showDetailBackdrop, setShowDetailBackdrop] = useState(false)
	const [showDeleteDetailBackdrop, setShowDeleteDetailBackdrop] = useState(false)
	const [updateHistory, setUpdateHistory] = useState()
	const [orderDetail, setOrderDetail] = useState()
	const [deleteOrderDetail, setDeleteOrderDetail] = useState()

	const getStatistics = useCallback(async () => {
		const { date, month, year } = timePick
		try {
			const response = await axios({
				url: `${DOMAIN}/store-manager/statistics/store/today?shift=${shift.morning}&date=${date}&month=${month}&year=${year}&user=${userFilter}`,
				headers: { Authorization: `Bearer ${token}` },
				method: 'GET',
				withCredentials: true,
			})

			let filteredOrders = {
				detail: [...(response.data?.detail || []), ...(response.data?.deletedDetail || [])],
			}

			if (orderFilter === 'transfer') {
				filteredOrders.detail = filteredOrders.detail.filter((order) => order?.detail?.payment === 'transfer')
			} else if (orderFilter === 'normal') {
				filteredOrders.detail = filteredOrders.detail.filter(
					(order) => order?.detail?.payment === 'cash' && !order?.detail?.app,
				)
			} else if (orderFilter === 'quickPurchase') {
				filteredOrders.detail = filteredOrders.detail.filter((order) => order?.detail?.quickPurchase === true)
			} else if (orderFilter === 'app') {
				filteredOrders.detail = filteredOrders.detail.filter(
					(order) => order?.detail?.app === true && order?.detail?.payment === 'cash',
				)
			} else if (orderFilter === 'send') {
				filteredOrders.detail = filteredOrders.detail.filter((order) => order?.detail?.status === 'transfer')
			} else if (orderFilter === 'receive') {
				filteredOrders.detail = filteredOrders.detail.filter((order) => order?.detail?.transferStore)
			} else if (orderFilter === 'cancel') {
				filteredOrders.detail = filteredOrders.detail.filter(
					(order) =>
						order?.detail?.status === 'confirm-delete-produce' ||
						order?.detail?.status === 'confirm-delete-ship' ||
						order?.detail?.status === 'confirm-delete-success',
				)
			}

			setStatistics(filteredOrders)
			setLoadingStatistics(false)
		} catch (error) {
			handleError(error)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}, [token, navigate, timePick, shift.morning, dispatch, userFilter, orderFilter])

	useEffect(() => {
		getStatistics()
	}, [getStatistics])

	const getUsers = useCallback(async () => {
		try {
			const response = await axios({
				url: api.GET_USERS_STORE_MANAGER_WITH_SM,
				headers: { Authorization: `Bearer ${token}` },
				method: 'GET',
				withCredentials: true,
			})
			setUsers(response.data)
		} catch (error) {
			handleError(error)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}, [token, dispatch, navigate])

	useEffect(() => {
		getUsers()
	}, [getUsers])

	const openImageViewer = (photo) => {
		setCurrentImage(photo)
		setIsViewerOpen(true)
	}

	const closeImageViewer = () => {
		setCurrentImage(null)
		setIsViewerOpen(false)
	}

	const setTime = (time) => {
		setTimePick(time)
	}

	const handleChangeUser = (e) => {
		setUserFilter(e.target.value)
	}

	const handleChangeOrder = (e) => {
		setOrderFilter(e.target.value)
	}

	const handleViewDetail = (order) => {
		setOrderDetail(order)
		setShowDetailBackdrop(true)
	}

	return {
		statistics,
		users,
		loadingStatistics,
		timePick,
		shift,
		userFilter,
		orderFilter,
		currentImage,
		isViewerOpen,
		showBackdrop,
		showDetailBackdrop,
		showDeleteDetailBackdrop,
		updateHistory,
		orderDetail,
		deleteOrderDetail,
		setTime,
		setShift,
		handleChangeUser,
		handleChangeOrder,
		openImageViewer,
		closeImageViewer,
		setShowBackdrop,
		setShowDetailBackdrop,
		setShowDeleteDetailBackdrop,
		setUpdateHistory,
		setOrderDetail,
		setDeleteOrderDetail,
		handleViewDetail,
	}
}
